import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Plus,
  Minus,
  ImageIcon,
  Sparkles,
  HelpCircle,
  Settings,
  Clock,
  X,
  Search,
  LogOut,
  Type,
  Palette,
  Badge,
} from 'lucide-react';
import AppleHeader from '../components/AppleHeader';

/* ─── Helpers ─── */

const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => (
  <section id={id} className="mb-16">
    <h2 className="text-[17px] font-bold text-black/90 mb-1">{title}</h2>
    <div className="h-[0.5px] bg-black/[0.06] mb-6" />
    {children}
  </section>
);

const Sub = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-8">
    <h3 className="text-[13px] font-medium text-[#3C3C43]/60 uppercase tracking-wide mb-3">{title}</h3>
    {children}
  </div>
);

const Swatch = ({ color, label, sub }: { color: string; label: string; sub?: string }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-[9px] border border-black/[0.06] shrink-0" style={{ background: color }} />
    <div>
      <div className="text-[13px] font-medium text-black/90">{label}</div>
      {sub && <div className="text-[11px] text-[#3C3C43]/60">{sub}</div>}
    </div>
  </div>
);

const Token = ({ name, value, preview }: { name: string; value: string; preview?: React.ReactNode }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-black/[0.04] last:border-0">
    <div className="flex items-center gap-3 min-w-0">
      {preview && <div className="shrink-0">{preview}</div>}
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-black/90 font-mono">{name}</div>
        <div className="text-[11px] text-[#3C3C43]/60">{value}</div>
      </div>
    </div>
  </div>
);

const UICatalog2: React.FC = () => {
  const [demoToggle, setDemoToggle] = useState(false);
  const [demoTab, setDemoTab] = useState<'uno' | 'dos' | 'tres'>('uno');
  const [demoCount, setDemoCount] = useState(2);

  return (
    <div className="relative p-4 pb-28 sm:p-8 sm:pb-8 max-w-3xl mx-auto bg-surface-mobile sm:bg-transparent min-h-screen">
      <AppleHeader title="Design System v2" />

      <p className="text-[13px] text-[#3C3C43]/60 -mt-2 mb-8 sm:mb-10 text-center sm:text-left">
        Referencia visual de los tokens Apple HIG usados en PruebaNuevaInterfaz y SeleccionarProductos.
      </p>

      {/* ━━━━━━━━━━━━━━━━━━━━ TABLE OF CONTENTS ━━━━━━━━━━━━━━━━━━━━ */}
      <nav className="mb-12 bg-[#F2F2F7] rounded-[13px] p-4">
        <div className="text-[13px] font-medium text-[#3C3C43]/60 uppercase tracking-wide mb-2">Contenidos</div>
        {[
          { id: 'typography', label: '1. Tipografía' },
          { id: 'colors', label: '2. Colores' },
          { id: 'spacing', label: '3. Espaciado' },
          { id: 'radius', label: '4. Border Radius' },
          { id: 'shadows', label: '5. Sombras' },
          { id: 'buttons', label: '6. Botones' },
          { id: 'inputs', label: '7. Inputs' },
          { id: 'headers', label: '8. Headers' },
          { id: 'lists', label: '9. Listas' },
          { id: 'sheets', label: '10. Bottom Sheets' },
          { id: 'badges', label: '11. Badges' },
          { id: 'popover', label: '12. Popover Menu' },
          { id: 'stepper', label: '13. Stepper / Counter' },
          { id: 'tabs', label: '14. Segmented Control' },
        ].map(item => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="flex items-center justify-between py-2 text-[13px] text-black/85 active:bg-black/[0.04] rounded-[7px] px-2 -mx-2 transition-colors"
          >
            <span>{item.label}</span>
            <ChevronRight size={14} className="text-[#3C3C43]/30" />
          </a>
        ))}
      </nav>

      {/* ━━━━━━━━━━━━━━━━━━━━ 1. TYPOGRAPHY ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="typography" title="1. Tipografía">
        <Sub title="Escala tipográfica — Solo 4 tamaños">
          <div className="bg-[#F2F2F7] rounded-[13px] overflow-hidden">
            <div className="px-4 py-3 border-b border-black/[0.04]">
              <span className="text-[17px] font-semibold text-black/90">17px — Títulos</span>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5">font-semibold / font-bold · Headers, nav bar, modals</div>
            </div>
            <div className="px-4 py-3 border-b border-black/[0.04]">
              <span className="text-[15px] font-normal text-black/90">15px — Body primario</span>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5">font-normal / font-medium / font-semibold · Labels, botones, inputs, descripciones</div>
            </div>
            <div className="px-4 py-3 border-b border-black/[0.04]">
              <span className="text-[13px] font-normal text-black/90">13px — Secundario / Caption</span>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5">font-normal / font-medium · Section headers (uppercase), subtítulos, nav items sidebar</div>
            </div>
            <div className="px-4 py-3">
              <span className="text-[11px] font-normal text-black/90">11px — Terciario / Micro</span>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5">font-normal / font-medium · Badges, micro-labels, company name en user card</div>
            </div>
          </div>
        </Sub>

        <Sub title="Tamaños eliminados (no usar)">
          <div className="bg-[#F2F2F7] rounded-[13px] px-4 py-3 space-y-1.5">
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-sm</code> (14px) → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[15px]</code></div>
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-xs</code> (12px) → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[13px]</code></div>
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-base</code> (16px) → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[15px]</code></div>
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-lg</code> (18px) → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[17px]</code></div>
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[10px]</code> → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[11px]</code></div>
            <div className="text-[13px] text-black/60"><code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[12px]</code> → usar <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">text-[13px]</code></div>
          </div>
        </Sub>

        <Sub title="Pesos y colores de texto">
          <div className="space-y-2">
            <div className="text-[15px] font-normal text-black/90">text-black/90 — Texto principal</div>
            <div className="text-[15px] font-normal text-[#3C3C43]/60">text-[#3C3C43]/60 — Texto secundario</div>
            <div className="text-[15px] font-normal text-[#3C3C43]/30">text-[#3C3C43]/30 — Placeholder / Disabled</div>
            <div className="text-[15px] font-semibold text-[#1644EB]">text-[#1644EB] — Acciones / Links</div>
            <div className="text-[15px] font-normal text-black/20">text-black/20 — Disabled action</div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 2. COLORS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="colors" title="2. Colores">
        <Sub title="Brand">
          <div className="grid grid-cols-2 gap-4">
            <Swatch color="#1644EB" label="brand-primary" sub="#1644EB — Acciones, links, CTA" />
            <Swatch color="#1238C8" label="brand-primary-hover" sub="#1238C8 — Hover state" />
            <Swatch color="#01269B" label="brand-deep" sub="#01269B — Desktop sidebar bg" />
            <Swatch color="#60E7F0" label="brand-accent" sub="#60E7F0 — Acento" />
          </div>
        </Sub>

        <Sub title="Surfaces">
          <div className="grid grid-cols-2 gap-4">
            <Swatch color="#F0F2F7" label="surface-bg" sub="#F0F2F7 — App background (desktop)" />
            <Swatch color="#FFFFFF" label="surface-card" sub="#FFFFFF — Cards, modals" />
            <Swatch color="#F7F8FB" label="surface-elevated" sub="#F7F8FB — Hover states" />
            <Swatch color="#F2F2F7" label="iOS system gray" sub="#F2F2F7 — Grouped list bg, tags" />
          </div>
        </Sub>

        <Sub title="Texto y separadores">
          <div className="grid grid-cols-2 gap-4">
            <Swatch color="rgba(0,0,0,0.90)" label="black/90" sub="Texto principal" />
            <Swatch color="rgba(60,60,67,0.60)" label="#3C3C43 @ 60%" sub="Texto secundario" />
            <Swatch color="rgba(60,60,67,0.30)" label="#3C3C43 @ 30%" sub="Placeholder" />
            <Swatch color="rgba(0,0,0,0.06)" label="black/[0.06]" sub="Separadores, borders" />
          </div>
        </Sub>

        <Sub title="Transparencias de marca">
          <div className="grid grid-cols-2 gap-4">
            <Swatch color="rgba(22,68,235,0.08)" label="#1644EB @ 8%" sub="Active nav item bg, plan badge bg" />
            <Swatch color="rgba(22,68,235,0.06)" label="#1644EB @ 6%" sub="Upsell banner bg" />
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 3. SPACING ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="spacing" title="3. Espaciado">
        <Sub title="Padding de página">
          <div className="space-y-1">
            <Token name="p-4 / px-4" value="16px — Mobile padding estándar" />
            <Token name="p-8 / px-8" value="32px — Desktop (sm:) padding" />
            <Token name="pb-28" value="112px — Mobile bottom padding (para bottom bar)" />
          </div>
        </Sub>

        <Sub title="Gaps frecuentes">
          <div className="space-y-1">
            <Token name="gap-0.5" value="2px — Nav items verticales" />
            <Token name="gap-2" value="8px — Chips, opciones compactas" />
            <Token name="gap-2.5" value="10px — Items dentro de card, popover nav items" />
            <Token name="gap-3" value="12px — List rows, icon + text" />
            <Token name="gap-4" value="16px — Grid de swatches, secciones" />
          </div>
        </Sub>

        <Sub title="Alturas de componentes">
          <div className="space-y-1">
            <Token name="h-10 (40px)" value="Botón estándar, header mobile del AppleHeader" />
            <Token name="h-11 (44px)" value="Header desktop AppleHeader, workspace card popover, tap targets iOS" />
            <Token name="h-14 (56px)" value="Sidebar mobile header bar" />
            <Token name="h-[5px]" value="Barra de progreso (créditos)" />
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 4. BORDER RADIUS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="radius" title="4. Border Radius">
        <Sub title="Regla de radios concéntricos">
          <div className="bg-[#F2F2F7] rounded-[13px] p-4 text-[13px] text-black/80 mb-4">
            <strong className="text-black/90">R_outer = R_inner + Padding</strong>
            <div className="text-[#3C3C43]/60 mt-1">Los elementos anidados siempre deben tener un radio menor que su contenedor.</div>
          </div>
        </Sub>

        <Sub title="Tokens de radio">
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[6px] bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">6</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-[6px]</div>
                <div className="text-[11px] text-[#3C3C43]/60">Icon containers dentro de popover (w-6 h-6), botones CTA dentro de cards</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[7px] bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">7</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-[7px]</div>
                <div className="text-[11px] text-[#3C3C43]/60">Nav items dentro de popover, company logo thumbnail</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[9px] bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">9</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-[9px]</div>
                <div className="text-[11px] text-[#3C3C43]/60">Workspace card, upgrade CTA bar dentro de popover</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">12</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-md (12px)</div>
                <div className="text-[11px] text-[#3C3C43]/60">Botones, inputs, filas interactivas, hamburguesa</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[13px] bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">13</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-[13px]</div>
                <div className="text-[11px] text-[#3C3C43]/60">Grouped list containers (iOS Settings style), content sections</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-[14px] bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">14</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-[14px]</div>
                <div className="text-[11px] text-[#3C3C43]/60">Popover menu flotante</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#F2F2F7] border border-black/[0.06] flex items-center justify-center text-[11px] text-[#3C3C43]/60 font-mono">∞</div>
              <div>
                <div className="text-[13px] font-medium text-black/90">rounded-full (9999px)</div>
                <div className="text-[11px] text-[#3C3C43]/60">Solo avatares circulares, pill badges, barra de progreso</div>
              </div>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 5. SHADOWS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="shadows" title="5. Sombras">
        <Sub title="Niveles de elevación">
          <div className="space-y-4">
            <div className="bg-white rounded-[13px] p-4 border border-black/[0.06]">
              <div className="text-[13px] font-medium text-black/90">Flat (sin sombra)</div>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5">Cards sobre mismo color → solo border. <code className="bg-[#F2F2F7] px-1 rounded text-[11px] font-mono">border border-black/[0.06]</code></div>
            </div>
            <div className="bg-white rounded-[13px] p-4" style={{ boxShadow: '0 0.5px 0 0 rgba(0,0,0,0.05)' }}>
              <div className="text-[13px] font-medium text-black/90">Separator</div>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5"><code className="bg-[#F2F2F7] px-1 rounded text-[11px] font-mono">0 0.5px 0 0 rgba(0,0,0,0.05)</code></div>
            </div>
            <div className="bg-white rounded-[14px] p-4" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)' }}>
              <div className="text-[13px] font-medium text-black/90">Elevated (Popover menu)</div>
              <div className="text-[11px] text-[#3C3C43]/60 mt-0.5"><code className="bg-[#F2F2F7] px-1 rounded text-[11px] font-mono">0 8px 40px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)</code></div>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 6. BUTTONS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="buttons" title="6. Botones">
        <Sub title="Primary CTA (generación)">
          <div className="space-y-3">
            <button className="w-full h-[52px] bg-[#1644EB] text-white rounded-[13px] text-[15px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <Sparkles size={18} />
              Generar imágenes
            </button>
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">h-[52px] bg-[#1644EB] rounded-[13px] text-[15px] font-semibold active:scale-[0.98]</code>
            </div>
          </div>
        </Sub>

        <Sub title="Text button (acciones, links)">
          <div className="space-y-3">
            <div className="flex items-center gap-6">
              <button className="text-[15px] font-normal text-[#1644EB] active:opacity-50 transition-opacity">
                <span className="flex items-center gap-1"><ChevronLeft size={18} /> Atrás</span>
              </button>
              <button className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity">
                Siguiente
              </button>
              <button className="text-[15px] font-semibold text-black/20 cursor-not-allowed">
                Disabled
              </button>
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">text-[15px] text-[#1644EB] active:opacity-50 transition-opacity</code>
            </div>
          </div>
        </Sub>

        <Sub title="Pill button (upsell, tags)">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-2 h-10 px-4 bg-[#1644EB] text-white rounded-full text-[15px] font-medium active:scale-[0.97] transition-transform">
                Ver planes <ChevronRight size={16} />
              </button>
              <button className="inline-flex items-center gap-2 h-10 px-4 bg-[#F2F2F7] text-black/85 rounded-full text-[13px] font-medium active:bg-black/[0.08] transition-colors">
                Filtro
              </button>
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">rounded-full h-10 px-4 active:scale-[0.97]</code>
            </div>
          </div>
        </Sub>

        <Sub title="Hamburger / Icon button">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-md text-black/60 active:bg-black/[0.04] transition-colors">
                <Settings size={20} />
              </button>
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-md text-black/60 active:bg-black/[0.04] transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">w-9 h-9 rounded-md text-black/60 active:bg-black/[0.04]</code>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 7. INPUTS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="inputs" title="7. Inputs">
        <Sub title="Search input">
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#3C3C43]/30" />
              <input
                type="text"
                placeholder="Buscar productos..."
                className="w-full pl-10 pr-3 h-10 rounded-md border border-black/[0.06] bg-[#F2F2F7] text-[15px] text-black/90 placeholder:text-[#3C3C43]/30 focus:ring-2 focus:ring-[#1644EB] focus:border-[#1644EB] outline-none transition-colors"
              />
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">h-10 rounded-md bg-[#F2F2F7] border-black/[0.06] text-[15px]</code>
            </div>
          </div>
        </Sub>

        <Sub title="Text area (prompt)">
          <div className="space-y-3">
            <textarea
              placeholder="Describí tu escenario ideal..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-[13px] border border-black/[0.06] bg-[#F2F2F7] text-[15px] text-black/90 placeholder:text-[#3C3C43]/30 focus:ring-2 focus:ring-[#1644EB] focus:border-[#1644EB] outline-none transition-colors resize-none"
            />
            <div className="text-[11px] text-[#3C3C43]/60">
              <code className="font-mono">rounded-[13px] bg-[#F2F2F7] border-black/[0.06] text-[15px]</code>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 8. HEADERS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="headers" title="8. Headers">
        <Sub title="Sidebar mobile header (solid white)">
          <div className="relative rounded-[13px] overflow-hidden border border-black/[0.06]">
            <div className="bg-white border-b border-black/[0.06]">
              <div className="relative flex items-center h-14 px-4">
                <button className="inline-flex items-center justify-center shrink-0 text-[#1644EB] active:opacity-50 transition-opacity">
                  <ChevronLeft size={22} />
                </button>
                <span className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-black/90">Estudio Virtual</span>
                <button className="ml-auto inline-flex items-center justify-center w-9 h-9 rounded-md text-black/60 active:bg-black/[0.04] transition-colors">
                  <Settings size={20} />
                </button>
              </div>
            </div>
            <div className="h-20 bg-gradient-to-b from-[#F2F2F7] to-white" />
          </div>
          <div className="text-[11px] text-[#3C3C43]/60 mt-2">
            <code className="font-mono">bg-white border-b border-black/[0.06] h-14</code>
          </div>
        </Sub>

        <Sub title="AppleHeader component (página interna)">
          <div className="rounded-[13px] overflow-hidden border border-black/[0.06] bg-white p-4">
            <div className="flex items-center justify-between h-10">
              <button className="inline-flex items-center gap-1 text-[#1644EB] active:opacity-50 transition-opacity text-[15px] font-normal">
                <ChevronLeft className="w-5 h-5" />
                <span>Atrás</span>
              </button>
              <h1 className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-black/90">Título de página</h1>
              <button className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity">
                Siguiente
              </button>
            </div>
          </div>
          <div className="text-[11px] text-[#3C3C43]/60 mt-2">
            <code className="font-mono">{'<AppleHeader title="..." onBack={fn} actionLabel="..." onAction={fn} />'}</code>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 9. LISTS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="lists" title="9. Listas">
        <Sub title="Grouped list (iOS Settings style)">
          <div className="bg-[#F2F2F7] rounded-[13px] overflow-hidden">
            {[
              { icon: <ImageIcon size={14} />, label: 'Estudio Virtual', active: true },
              { icon: <Clock size={14} />, label: 'Historial', active: false },
              { icon: <HelpCircle size={14} />, label: 'Ayuda', active: false },
              { icon: <Settings size={14} />, label: 'Configuración', active: false },
            ].map((item, i) => (
              <div
                key={i}
                className={`flex items-center gap-2.5 px-4 py-2.5 transition-colors ${
                  item.active ? 'bg-[#1644EB]/[0.08]' : 'active:bg-black/[0.04]'
                } ${i > 0 ? 'border-t border-black/[0.04]' : ''}`}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 ${
                  item.active ? 'bg-[#1644EB] text-white' : 'bg-white text-[#3C3C43]/70'
                }`}>
                  {item.icon}
                </span>
                <span className={`text-[13px] ${item.active ? 'text-[#1644EB] font-medium' : 'text-black/85'}`}>{item.label}</span>
                {!item.active && <ChevronRight size={14} className="ml-auto text-[#3C3C43]/30" />}
              </div>
            ))}
          </div>
        </Sub>

        <Sub title="Option row con check">
          <div className="bg-[#F2F2F7] rounded-[13px] overflow-hidden">
            {['Sugerido por IA', 'Fondo blanco', 'Personalizado'].map((label, i) => (
              <div
                key={i}
                className={`flex items-center justify-between px-4 py-3 ${i > 0 ? 'border-t border-black/[0.04]' : ''} active:bg-black/[0.04] transition-colors`}
              >
                <span className="text-[15px] text-black/90">{label}</span>
                {i === 0 && <Check size={18} className="text-[#1644EB]" />}
              </div>
            ))}
          </div>
        </Sub>

        <Sub title="Add option row (texto, colores, logo)">
          <div className="flex gap-2">
            {[
              { Icon: Type, label: 'Texto' },
              { Icon: Palette, label: 'Colores' },
              { Icon: Badge, label: 'Logo' },
            ].map(({ Icon, label }) => (
              <button
                key={label}
                className="flex-1 flex flex-col items-center gap-1.5 py-3 bg-[#F2F2F7] rounded-[13px] active:bg-black/[0.08] transition-colors"
              >
                <Icon size={20} className="text-[#3C3C43]/70" />
                <span className="text-[11px] text-[#3C3C43]/60">{label}</span>
              </button>
            ))}
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 10. BOTTOM SHEETS ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="sheets" title="10. Bottom Sheets">
        <Sub title="Drag handle + header + content">
          <div className="bg-white rounded-t-[13px] border border-black/[0.06] overflow-hidden">
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-9 h-1 rounded-full bg-black/[0.15]" />
            </div>
            {/* Sheet header */}
            <div className="flex items-center justify-between px-4 h-11">
              <button className="text-[15px] text-[#1644EB] active:opacity-50 transition-opacity">Cancelar</button>
              <h3 className="text-[15px] font-semibold text-black/90">Formato</h3>
              <button className="text-[15px] font-semibold text-[#1644EB] active:opacity-50 transition-opacity">Listo</button>
            </div>
            <div className="h-[0.5px] bg-black/[0.06]" />
            {/* Sheet content */}
            <div className="p-4 space-y-2">
              {['Posteo (4:5)', 'Historias/Reels (9:16)', 'Cuadrado (1:1)'].map((label, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 px-3 rounded-[9px] active:bg-[#F2F2F7] transition-colors">
                  <span className="text-[15px] text-black/90">{label}</span>
                  {i === 0 && <Check size={18} className="text-[#1644EB]" />}
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-[#3C3C43]/60 mt-2">
            Drag handle: <code className="font-mono">w-9 h-1 rounded-full bg-black/[0.15]</code>
            <br />
            Slide up: <code className="font-mono">translateY(100%) → translateY(0) 300ms ease-out</code>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 11. BADGES ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="badges" title="11. Badges">
        <Sub title="Variantes">
          <div className="flex flex-wrap gap-3">
            <span className="text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">Gratis</span>
            <span className="text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">Básico</span>
            <span className="text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">Pro</span>
            <span className="text-[11px] font-semibold text-white bg-[#1644EB] px-2 py-0.5 rounded-full">Nuevo</span>
            <span className="text-[11px] font-medium text-[#3C3C43]/60 bg-[#F2F2F7] px-1.5 py-0.5 rounded-full">Pronto</span>
          </div>
          <div className="text-[11px] text-[#3C3C43]/60 mt-2">
            <code className="font-mono">text-[11px] font-medium rounded-full px-1.5 py-0.5</code>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 12. POPOVER MENU ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="popover" title="12. Popover Menu">
        <Sub title="Floating popover (menú mobile)">
          <div className="flex justify-end">
            <div className="w-[272px]">
              <div className="bg-white rounded-[14px] overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.06)' }}>
                {/* Credits bar */}
                <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2.5">
                  <div className="px-1 py-0.5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[13px] text-black/80">5 fotos gratuitas restantes</span>
                    </div>
                    <div className="h-[5px] bg-[#F2F2F7] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#1644EB] w-[50%]" />
                    </div>
                  </div>

                  {/* Upgrade CTA */}
                  <div className="flex items-center gap-2.5 bg-[#1644EB] rounded-[9px] px-3 h-[44px]">
                    <Sparkles size={17} className="text-white shrink-0" fill="currentColor" strokeWidth={0} />
                    <span className="text-[13px] text-white flex-1">Mejorá tu plan</span>
                    <span className="shrink-0 text-[13px] font-semibold text-[#1644EB] bg-white px-2.5 py-1 rounded-[6px]">
                      Ver planes
                    </span>
                  </div>

                  {/* Workspace card */}
                  <div className="flex items-center gap-2.5 bg-[#F2F2F7] rounded-[9px] px-2.5 h-[44px]">
                    <div className="w-8 h-8 rounded-[7px] bg-white shrink-0 flex items-center justify-center">
                      <Sparkles size={14} className="text-[#3C3C43]/30" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-black/90 truncate">Mati</div>
                      <div className="text-[11px] text-[#3C3C43]/60 truncate">Prueba</div>
                    </div>
                    <span className="shrink-0 text-[11px] font-medium text-[#1644EB] bg-[#1644EB]/[0.08] px-1.5 py-0.5 rounded-full">Gratis</span>
                  </div>
                </div>

                {/* Nav items */}
                <div className="px-1.5 pb-0.5">
                  {[
                    { icon: <ImageIcon size={14} />, label: 'Estudio Virtual', active: true },
                    { icon: <Clock size={14} />, label: 'Historial', active: false },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] transition-colors ${
                        item.active ? 'bg-[#1644EB]/[0.08] text-[#1644EB]' : 'text-black/85'
                      }`}
                    >
                      <span className={`w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 ${
                        item.active ? 'bg-[#1644EB] text-white' : 'bg-[#F2F2F7] text-[#3C3C43]/70'
                      }`}>
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

                <div className="px-1.5 py-0.5">
                  <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-black/85">
                    <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-[#F2F2F7] text-[#3C3C43]/70">
                      <HelpCircle size={14} />
                    </span>
                    <span>Ayuda</span>
                  </div>
                </div>

                <div className="mx-3 h-[0.5px] bg-black/[0.06]" />

                <div className="px-1.5 py-0.5 pb-1.5">
                  <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-[7px] text-[13px] text-black/85">
                    <span className="w-6 h-6 flex items-center justify-center rounded-[6px] shrink-0 bg-[#F2F2F7] text-[#3C3C43]/70">
                      <LogOut size={14} />
                    </span>
                    <span>Cerrar sesión</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-[#3C3C43]/60 mt-2">
            <code className="font-mono">w-[272px] rounded-[14px] shadow-[0_8px_40px_rgba(0,0,0,0.12)]</code>
            <br />
            Animación: <code className="font-mono">scale-[0.9] → scale-100, opacity-0 → opacity-100, 200ms ease-out</code>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 13. STEPPER ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="stepper" title="13. Stepper / Counter">
        <Sub title="Cantidad de imágenes">
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-6">
              <button
                onClick={() => setDemoCount(Math.max(1, demoCount - 1))}
                className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center text-black/60 active:bg-black/[0.08] transition-colors"
              >
                <Minus size={18} />
              </button>
              <span className="text-[22px] font-semibold text-black/90 w-8 text-center tabular-nums">{demoCount}</span>
              <button
                onClick={() => setDemoCount(Math.min(10, demoCount + 1))}
                className="w-9 h-9 rounded-full bg-[#F2F2F7] flex items-center justify-center text-black/60 active:bg-black/[0.08] transition-colors"
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="text-[11px] text-[#3C3C43]/60 text-center">
              <code className="font-mono">w-9 h-9 rounded-full bg-[#F2F2F7] · text-[22px] font-semibold tabular-nums</code>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━━━━━━━━━━━━━━━━━ 14. SEGMENTED CONTROL ━━━━━━━━━━━━━━━━━━━━ */}
      <Section id="tabs" title="14. Segmented Control">
        <Sub title="Tabs con indicador animado">
          <div className="space-y-3">
            <div className="bg-[#F2F2F7] rounded-[9px] p-1 flex relative">
              {/* Sliding indicator */}
              <div
                className="absolute top-1 bottom-1 bg-white rounded-[7px] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out"
                style={{
                  width: `calc(${100 / 3}% - 2px)`,
                  left: `calc(${(['uno', 'dos', 'tres'].indexOf(demoTab)) * (100 / 3)}% + 1px)`,
                }}
              />
              {(['uno', 'dos', 'tres'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setDemoTab(tab)}
                  className={`relative z-10 flex-1 text-[13px] font-medium py-1.5 text-center transition-colors ${
                    demoTab === tab ? 'text-black/90' : 'text-[#3C3C43]/60'
                  }`}
                >
                  {tab === 'uno' ? 'Automático' : tab === 'dos' ? 'Personalizado' : 'Creativo'}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              Container: <code className="font-mono">bg-[#F2F2F7] rounded-[9px] p-1</code>
              <br />
              Indicator: <code className="font-mono">bg-white rounded-[7px] shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-all 300ms</code>
              <br />
              Text: <code className="font-mono">text-[13px] font-medium</code>
            </div>
          </div>
        </Sub>

        <Sub title="Toggle / Switch row">
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-[#F2F2F7] rounded-[13px] px-4 py-3">
              <div>
                <div className="text-[15px] text-black/90">Agregar modelo</div>
                <div className="text-[13px] text-[#3C3C43]/60">Una persona con tu producto</div>
              </div>
              <button
                onClick={() => setDemoToggle(!demoToggle)}
                className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 ${demoToggle ? 'bg-[#1644EB]' : 'bg-[#E5E5EA]'}`}
              >
                <div className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform duration-200 ${demoToggle ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
              </button>
            </div>
            <div className="text-[11px] text-[#3C3C43]/60">
              Track: <code className="font-mono">w-[51px] h-[31px] rounded-full</code>
              <br />
              Thumb: <code className="font-mono">w-[27px] h-[27px] rounded-full bg-white shadow</code>
              <br />
              On: <code className="font-mono">bg-[#1644EB]</code> · Off: <code className="font-mono">bg-[#E5E5EA]</code>
            </div>
          </div>
        </Sub>
      </Section>

      {/* ━━━━ Footer ━━━━ */}
      <div className="mt-16 pt-6 border-t border-black/[0.06] text-center">
        <p className="text-[13px] text-[#3C3C43]/60">
          Atlas Design System v2 — Apple HIG–inspired tokens
        </p>
        <p className="text-[11px] text-[#3C3C43]/30 mt-1">
          Referencia interna para desarrollo
        </p>
      </div>
    </div>
  );
};

export default UICatalog2;
